/**
 * Custom Error Types
 *
 * Defines a hierarchy of custom error classes for consistent error handling
 * across the application. Each error type represents a specific category
 * of failure with appropriate metadata.
 */

// =============================================================================
// ERROR CODES
// =============================================================================

/**
 * Standardized error codes for categorization and handling.
 */
export enum ErrorCode {
  // Generic errors
  UNKNOWN = 'UNKNOWN',
  INTERNAL = 'INTERNAL',

  // Validation errors
  VALIDATION_FAILED = 'VALIDATION_FAILED',
  INVALID_INPUT = 'INVALID_INPUT',
  MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',

  // Authentication errors
  AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  TOKEN_INVALID = 'TOKEN_INVALID',

  // Authorization errors
  AUTHORIZATION_FAILED = 'AUTHORIZATION_FAILED',
  INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS',
  ACCESS_DENIED = 'ACCESS_DENIED',

  // Network errors
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT = 'TIMEOUT',
  CONNECTION_REFUSED = 'CONNECTION_REFUSED',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',

  // Resource errors
  NOT_FOUND = 'NOT_FOUND',
  RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',

  // Conflict errors
  CONFLICT = 'CONFLICT',
  DUPLICATE_ENTRY = 'DUPLICATE_ENTRY',
  CONCURRENT_MODIFICATION = 'CONCURRENT_MODIFICATION',
}

// =============================================================================
// ERROR SEVERITY
// =============================================================================

/**
 * Indicates the severity level of an error for logging and alerting purposes.
 */
export enum ErrorSeverity {
  /** Informational, non-critical issues */
  LOW = 'low',
  /** Standard errors that need attention */
  MEDIUM = 'medium',
  /** Critical errors requiring immediate attention */
  HIGH = 'high',
  /** System-wide failures */
  CRITICAL = 'critical',
}

// =============================================================================
// ERROR METADATA INTERFACE
// =============================================================================

/**
 * Additional context that can be attached to any error.
 */
export interface ErrorMetadata {
  /** The field that caused the error (for validation errors) */
  field?: string;
  /** The invalid value that caused the error */
  value?: unknown;
  /** Expected value or format */
  expected?: string;
  /** Resource identifier */
  resourceId?: string;
  /** Resource type */
  resourceType?: string;
  /** HTTP status code if applicable */
  statusCode?: number;
  /** Original error that caused this error */
  cause?: Error;
  /** Request ID for tracing */
  requestId?: string;
  /** Additional custom data */
  [key: string]: unknown;
}

// =============================================================================
// BASE APP ERROR
// =============================================================================

/**
 * Base error class for all application errors.
 * Extends the native Error class with additional metadata for better error handling.
 */
export class AppError extends Error {
  /** Error code for categorization */
  public readonly code: ErrorCode;

  /** Error severity level */
  public readonly severity: ErrorSeverity;

  /** Whether this error is recoverable */
  public readonly isRecoverable: boolean;

  /** Additional error metadata */
  public readonly metadata: ErrorMetadata;

  /** Timestamp when the error occurred */
  public readonly timestamp: Date;

  /** Whether this error should be reported to external services */
  public readonly shouldReport: boolean;

  constructor(
    message: string,
    code: ErrorCode = ErrorCode.UNKNOWN,
    options: {
      severity?: ErrorSeverity;
      isRecoverable?: boolean;
      metadata?: ErrorMetadata;
      shouldReport?: boolean;
    } = {}
  ) {
    super(message);

    this.name = 'AppError';
    this.code = code;
    this.severity = options.severity ?? ErrorSeverity.MEDIUM;
    this.isRecoverable = options.isRecoverable ?? true;
    this.metadata = options.metadata ?? {};
    this.timestamp = new Date();
    this.shouldReport = options.shouldReport ?? true;

    // Maintain proper stack trace in V8 environments
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }

    // Set the prototype explicitly for proper instanceof checks
    Object.setPrototypeOf(this, new.target.prototype);
  }

  /**
   * Creates a serializable representation of the error.
   */
  public toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      severity: this.severity,
      isRecoverable: this.isRecoverable,
      metadata: this.metadata,
      timestamp: this.timestamp.toISOString(),
    };
  }
}

// =============================================================================
// VALIDATION ERROR
// =============================================================================

/**
 * Error for validation failures (invalid input, missing fields, etc.).
 */
export class ValidationError extends AppError {
  /** List of validation errors when multiple fields fail */
  public readonly validationErrors: Array<{
    field: string;
    message: string;
    value?: unknown;
  }>;

  constructor(
    message: string,
    options: {
      code?: ErrorCode;
      field?: string;
      value?: unknown;
      expected?: string;
      validationErrors?: Array<{ field: string; message: string; value?: unknown }>;
      metadata?: ErrorMetadata;
    } = {}
  ) {
    super(message, options.code ?? ErrorCode.VALIDATION_FAILED, {
      severity: ErrorSeverity.LOW,
      isRecoverable: true,
      shouldReport: false,
      metadata: {
        field: options.field,
        value: options.value,
        expected: options.expected,
        ...options.metadata,
      },
    });

    this.name = 'ValidationError';
    this.validationErrors = options.validationErrors ?? [];

    if (options.field && !this.validationErrors.length) {
      this.validationErrors.push({
        field: options.field,
        message,
        value: options.value,
      });
    }

    Object.setPrototypeOf(this, ValidationError.prototype);
  }

  /**
   * Creates a ValidationError with multiple field errors.
   */
  static fromMultiple(
    errors: Array<{ field: string; message: string; value?: unknown }>
  ): ValidationError {
    const message =
      errors.length === 1
        ? errors[0].message
        : `Errori di validazione su ${errors.length} campi`;

    return new ValidationError(message, { validationErrors: errors });
  }
}

// =============================================================================
// AUTHENTICATION ERROR
// =============================================================================

/**
 * Error for authentication failures (login, session, token issues).
 */
export class AuthenticationError extends AppError {
  constructor(
    message: string,
    options: {
      code?: ErrorCode;
      metadata?: ErrorMetadata;
    } = {}
  ) {
    super(message, options.code ?? ErrorCode.AUTHENTICATION_FAILED, {
      severity: ErrorSeverity.MEDIUM,
      isRecoverable: true,
      shouldReport: true,
      metadata: options.metadata,
    });

    this.name = 'AuthenticationError';
    Object.setPrototypeOf(this, AuthenticationError.prototype);
  }

  /**
   * Creates an error for expired sessions.
   */
  static sessionExpired(): AuthenticationError {
    return new AuthenticationError('La sessione è scaduta. Effettua nuovamente il login.', {
      code: ErrorCode.SESSION_EXPIRED,
    });
  }

  /**
   * Creates an error for invalid credentials.
   */
  static invalidCredentials(): AuthenticationError {
    return new AuthenticationError('Credenziali non valide.', {
      code: ErrorCode.INVALID_CREDENTIALS,
    });
  }

  /**
   * Creates an error for expired tokens.
   */
  static tokenExpired(): AuthenticationError {
    return new AuthenticationError('Il token di autenticazione è scaduto.', {
      code: ErrorCode.TOKEN_EXPIRED,
    });
  }
}

// =============================================================================
// AUTHORIZATION ERROR
// =============================================================================

/**
 * Error for authorization failures (permission denied, access control).
 */
export class AuthorizationError extends AppError {
  /** The resource that access was denied to */
  public readonly resource?: string;

  /** The action that was attempted */
  public readonly action?: string;

  constructor(
    message: string,
    options: {
      code?: ErrorCode;
      resource?: string;
      action?: string;
      metadata?: ErrorMetadata;
    } = {}
  ) {
    super(message, options.code ?? ErrorCode.AUTHORIZATION_FAILED, {
      severity: ErrorSeverity.MEDIUM,
      isRecoverable: false,
      shouldReport: true,
      metadata: {
        resourceType: options.resource,
        ...options.metadata,
      },
    });

    this.name = 'AuthorizationError';
    this.resource = options.resource;
    this.action = options.action;
    Object.setPrototypeOf(this, AuthorizationError.prototype);
  }

  /**
   * Creates an error for insufficient permissions.
   */
  static insufficientPermissions(
    action: string,
    resource?: string
  ): AuthorizationError {
    const resourceText = resource ? ` su ${resource}` : '';
    return new AuthorizationError(
      `Non hai i permessi necessari per ${action}${resourceText}.`,
      {
        code: ErrorCode.INSUFFICIENT_PERMISSIONS,
        action,
        resource,
      }
    );
  }

  /**
   * Creates an error for access denied.
   */
  static accessDenied(resource?: string): AuthorizationError {
    const resourceText = resource ? ` a ${resource}` : '';
    return new AuthorizationError(`Accesso negato${resourceText}.`, {
      code: ErrorCode.ACCESS_DENIED,
      resource,
    });
  }
}

// =============================================================================
// NETWORK ERROR
// =============================================================================

/**
 * Error for network-related failures (connection, timeout, server errors).
 */
export class NetworkError extends AppError {
  /** HTTP status code if applicable */
  public readonly statusCode?: number;

  /** The URL that failed */
  public readonly url?: string;

  constructor(
    message: string,
    options: {
      code?: ErrorCode;
      statusCode?: number;
      url?: string;
      metadata?: ErrorMetadata;
    } = {}
  ) {
    super(message, options.code ?? ErrorCode.NETWORK_ERROR, {
      severity: ErrorSeverity.MEDIUM,
      isRecoverable: true,
      shouldReport: true,
      metadata: {
        statusCode: options.statusCode,
        ...options.metadata,
      },
    });

    this.name = 'NetworkError';
    this.statusCode = options.statusCode;
    this.url = options.url;
    Object.setPrototypeOf(this, NetworkError.prototype);
  }

  /**
   * Creates a timeout error.
   */
  static timeout(url?: string): NetworkError {
    return new NetworkError(
      'La richiesta ha impiegato troppo tempo. Riprova più tardi.',
      {
        code: ErrorCode.TIMEOUT,
        url,
      }
    );
  }

  /**
   * Creates a connection refused error.
   */
  static connectionRefused(url?: string): NetworkError {
    return new NetworkError('Impossibile connettersi al server. Verifica la connessione.', {
      code: ErrorCode.CONNECTION_REFUSED,
      url,
    });
  }

  /**
   * Creates a service unavailable error.
   */
  static serviceUnavailable(url?: string): NetworkError {
    return new NetworkError('Il servizio non è attualmente disponibile. Riprova più tardi.', {
      code: ErrorCode.SERVICE_UNAVAILABLE,
      statusCode: 503,
      url,
    });
  }

  /**
   * Creates a network error from an HTTP status code.
   */
  static fromStatusCode(statusCode: number, url?: string): NetworkError {
    const messages: Record<number, string> = {
      400: 'Richiesta non valida.',
      401: 'Autenticazione richiesta.',
      403: 'Accesso non autorizzato.',
      404: 'Risorsa non trovata.',
      408: 'Timeout della richiesta.',
      429: 'Troppe richieste. Riprova più tardi.',
      500: 'Errore interno del server.',
      502: 'Gateway non valido.',
      503: 'Servizio non disponibile.',
      504: 'Timeout del gateway.',
    };

    const message = messages[statusCode] ?? `Errore del server (${statusCode}).`;

    return new NetworkError(message, { statusCode, url });
  }
}

// =============================================================================
// NOT FOUND ERROR
// =============================================================================

/**
 * Error for resource not found failures.
 */
export class NotFoundError extends AppError {
  /** The type of resource that was not found */
  public readonly resourceType: string;

  /** The identifier of the resource that was not found */
  public readonly resourceId?: string;

  constructor(
    resourceType: string,
    resourceId?: string,
    options: {
      message?: string;
      metadata?: ErrorMetadata;
    } = {}
  ) {
    const defaultMessage = resourceId
      ? `${resourceType} con ID "${resourceId}" non trovato.`
      : `${resourceType} non trovato.`;

    super(options.message ?? defaultMessage, ErrorCode.NOT_FOUND, {
      severity: ErrorSeverity.LOW,
      isRecoverable: false,
      shouldReport: false,
      metadata: {
        resourceType,
        resourceId,
        ...options.metadata,
      },
    });

    this.name = 'NotFoundError';
    this.resourceType = resourceType;
    this.resourceId = resourceId;
    Object.setPrototypeOf(this, NotFoundError.prototype);
  }
}

// =============================================================================
// CONFLICT ERROR
// =============================================================================

/**
 * Error for conflict situations (duplicate entries, concurrent modifications).
 */
export class ConflictError extends AppError {
  /** The type of resource that has a conflict */
  public readonly resourceType?: string;

  /** The identifier of the conflicting resource */
  public readonly resourceId?: string;

  constructor(
    message: string,
    options: {
      code?: ErrorCode;
      resourceType?: string;
      resourceId?: string;
      metadata?: ErrorMetadata;
    } = {}
  ) {
    super(message, options.code ?? ErrorCode.CONFLICT, {
      severity: ErrorSeverity.MEDIUM,
      isRecoverable: true,
      shouldReport: false,
      metadata: {
        resourceType: options.resourceType,
        resourceId: options.resourceId,
        ...options.metadata,
      },
    });

    this.name = 'ConflictError';
    this.resourceType = options.resourceType;
    this.resourceId = options.resourceId;
    Object.setPrototypeOf(this, ConflictError.prototype);
  }

  /**
   * Creates an error for duplicate entries.
   */
  static duplicate(resourceType: string, field?: string): ConflictError {
    const fieldText = field ? ` per il campo "${field}"` : '';
    return new ConflictError(
      `Esiste già un ${resourceType}${fieldText} con questo valore.`,
      {
        code: ErrorCode.DUPLICATE_ENTRY,
        resourceType,
        metadata: { field },
      }
    );
  }

  /**
   * Creates an error for concurrent modifications.
   */
  static concurrentModification(resourceType: string): ConflictError {
    return new ConflictError(
      `Il ${resourceType} è stato modificato da un altro utente. Ricarica la pagina e riprova.`,
      {
        code: ErrorCode.CONCURRENT_MODIFICATION,
        resourceType,
      }
    );
  }
}

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard for AppError.
 */
export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}

/**
 * Type guard for ValidationError.
 */
export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

/**
 * Type guard for AuthenticationError.
 */
export function isAuthenticationError(error: unknown): error is AuthenticationError {
  return error instanceof AuthenticationError;
}

/**
 * Type guard for AuthorizationError.
 */
export function isAuthorizationError(error: unknown): error is AuthorizationError {
  return error instanceof AuthorizationError;
}

/**
 * Type guard for NetworkError.
 */
export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

/**
 * Type guard for NotFoundError.
 */
export function isNotFoundError(error: unknown): error is NotFoundError {
  return error instanceof NotFoundError;
}

/**
 * Type guard for ConflictError.
 */
export function isConflictError(error: unknown): error is ConflictError {
  return error instanceof ConflictError;
}
